import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send, X, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
 bookingChatUnreadQueryKey,
 fetchBookingChatMessages,
 markBookingChatRead,
 sendBookingChatMessage,
 sendBookingChatTyping,
} from '../../services/bookingChatService';
import { appendBookingChatMessage, CHAT_CONNECTION, useBookingChatRealtime } from '../../hooks/useBookingChatRealtime';
import { POLL_INTERVALS, smartRefetchInterval, smartQueryDefaults } from '../../utils/queryPolling';

const roleLabel = (role) => {
 if (role === 'admin') return 'Admin';
 if (role === 'vendor') return 'Vendor';
 if (role === 'client') return 'Client';
 return role || 'User';
};

const roleBubbleClass = (role, isMine) => {
 if (isMine) {
 return 'bg-black text-white rounded-2xl rounded-br-md';
 }
 if (role === 'admin') {
 return 'bg-amber-50 text-amber-950 border border-amber-200 rounded-2xl rounded-bl-md';
 }
 if (role === 'vendor') {
 return 'bg-blue-50 text-ink border border-border rounded-2xl rounded-bl-md';
 }
 return 'bg-gray-100 text-gray-900 border border-gray-200 rounded-2xl rounded-bl-md';
};

/**
 * Booking-scoped chat: client, vendor, and admin share the same thread (Reverb + REST fallback).
 */
const BookingChatPanel = ({ bookingId, isAdmin = false, onClose, className = '' }) => {
 const { user } = useAuth();
 const queryClient = useQueryClient();
 const bottomRef = useRef(null);
 const [draft, setDraft] = useState('');
 const baselineLastMessageId = useRef(null);
 const lastTypingSentAt = useRef(0);

 const queryKey = useMemo(() => ['bookingChat', bookingId, isAdmin], [bookingId, isAdmin]);
 const unreadKey = useMemo(() => bookingChatUnreadQueryKey(bookingId, isAdmin), [bookingId, isAdmin]);

 const { connectionStatus, typingText } = useBookingChatRealtime(bookingId, {
 enabled: Boolean(bookingId),
 currentUserId: user?.id,
 onMessage: (message) => {
 appendBookingChatMessage(queryClient, bookingId, isAdmin, message);
 queryClient.invalidateQueries({ queryKey: unreadKey });
 },
 });

 const { data, isLoading, error, isFetching } = useQuery({
 queryKey,
 queryFn: () => fetchBookingChatMessages(bookingId, { limit: 200 }, isAdmin),
 enabled: Boolean(bookingId),
 refetchInterval:
 connectionStatus === CHAT_CONNECTION.LIVE
 ? false
 : smartRefetchInterval(POLL_INTERVALS.CHAT_FALLBACK),
 ...smartQueryDefaults,
 });

 const messages = useMemo(
 () => (Array.isArray(data?.data) ? data.data : []),
 [data]
 );
 const meta = data?.meta;

 const messageSignature = useMemo(() => {
 const last = messages[messages.length - 1];
 return `${messages.length}:${last?.id ?? ''}`;
 }, [messages]);

 useEffect(() => {
 bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
 }, [messages.length]);

 useEffect(() => {
 if (typeof Notification === 'undefined' || typeof document === 'undefined') return;
 if (Notification.permission !== 'default') return;
 const t = setTimeout(() => {
 Notification.requestPermission().catch(() => {});
 }, 900);
 return () => clearTimeout(t);
 }, []);

 useEffect(() => {
 if (!bookingId || isLoading || error) return;
 const t = setTimeout(() => {
 markBookingChatRead(bookingId, isAdmin)
 .then(() => {
 queryClient.invalidateQueries({ queryKey: unreadKey });
 queryClient.invalidateQueries({ queryKey: ['bookings'] });
 queryClient.invalidateQueries({ queryKey: ['vendorBookings'] });
 })
 .catch(() => {});
 }, 400);
 return () => clearTimeout(t);
 }, [bookingId, isAdmin, isLoading, error, messageSignature, queryClient, unreadKey]);

 useEffect(() => {
 return () => {
 if (bookingId) {
 markBookingChatRead(bookingId, isAdmin).catch(() => {});
 }
 };
 }, [bookingId, isAdmin]);

 useEffect(() => {
 if (isLoading || error) return;
 const last = messages[messages.length - 1];
 const lastId = last?.id ?? null;
 if (baselineLastMessageId.current === null) {
 baselineLastMessageId.current = lastId;
 return;
 }
 if (!lastId || lastId === baselineLastMessageId.current) return;
 baselineLastMessageId.current = lastId;
 if (!last || last.user_id === user?.id) return;
 if (typeof document === 'undefined' || !document.hidden) return;
 if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
 try {
 const n = new Notification('New booking message', {
 body: (last.body || '').slice(0, 120),
 tag: `booking-chat-${bookingId}`,
 });
 n.onclick = () => {
 try {
 window.focus();
 } catch (_) {
 /* ignore */
 }
 };
 } catch (_) {
 /* ignore */
 }
 }, [messages, isLoading, error, user?.id, bookingId]);

 const sendMutation = useMutation({
 mutationFn: (body) => {
 const clientMsgId = `cmsg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
 return sendBookingChatMessage(bookingId, { body, client_msg_id: clientMsgId }, isAdmin);
 },
 onSuccess: () => {
 queryClient.invalidateQueries({ queryKey });
 queryClient.invalidateQueries({ queryKey: unreadKey });
 setDraft('');
 },
 });

 const handleSend = (e) => {
 e.preventDefault();
 const text = draft.trim();
 if (!text || sendMutation.isPending) return;
 sendMutation.mutate(text);
 };

 const handleKeyDown = (e) => {
 if (e.key !== 'Enter' || e.shiftKey) return;
 e.preventDefault();
 handleSend(e);
 };

 const notifyTyping = () => {
 const now = Date.now();
 if (now - lastTypingSentAt.current < 2000) {
 return;
 }
 lastTypingSentAt.current = now;
 sendBookingChatTyping(bookingId, isAdmin).catch(() => {});
 };

 const handleDraftChange = (e) => {
 setDraft(e.target.value);
 if (e.target.value.trim()) {
 notifyTyping();
 }
 };

 return (
 <div
 className={`flex flex-col bg-surface dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden ${className}`}
 >
 <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 bg-surface-muted">
 <div className="flex items-center gap-2 min-w-0">
 <MessageSquare className="w-5 h-5 text-gray-700 shrink-0" />
 <div className="min-w-0">
 <h3 className="text-sm font-semibold text-gray-900 truncate">Booking chat</h3>
 {meta?.booking_reference && (
 <p className="text-xs text-gray-500 truncate">{meta.booking_reference}</p>
 )}
 </div>
 </div>
 <div className="flex items-center gap-2 shrink-0">
 <span
 className={`inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full ${
 connectionStatus === CHAT_CONNECTION.LIVE
 ? 'bg-green-100 text-green-800'
 : connectionStatus === CHAT_CONNECTION.CONNECTING
 ? 'bg-amber-100 text-amber-800'
 : 'bg-gray-100 text-gray-600'
 }`}
 aria-live="polite"
 >
 <span
 className={`w-1.5 h-1.5 rounded-full ${
 connectionStatus === CHAT_CONNECTION.LIVE
 ? 'bg-green-500'
 : connectionStatus === CHAT_CONNECTION.CONNECTING
 ? 'bg-amber-500 animate-pulse'
 : 'bg-gray-400'
 }`}
 />
 {connectionStatus === CHAT_CONNECTION.LIVE
 ? 'Live'
 : connectionStatus === CHAT_CONNECTION.CONNECTING
 ? 'Connecting'
 : 'Polling'}
 </span>
 {isFetching && !isLoading && (
 <span className="text-xs text-gray-400" aria-live="polite">
 Updating…
 </span>
 )}
 {onClose && (
 <button
 type="button"
 onClick={onClose}
 className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-200"
 aria-label="Close chat"
 >
 <X className="w-5 h-5" />
 </button>
 )}
 </div>
 </div>

 <div className="flex-1 min-h-[220px] max-h-[360px] overflow-y-auto px-3 py-3 space-y-3 bg-surface dark:bg-gray-900">
 {isLoading && (
 <div className="flex justify-center py-8 text-gray-500">
 <Loader2 className="w-6 h-6 animate-spin" />
 </div>
 )}
 {error && (
 <div className="rounded-lg bg-red-50 border border-red-100 text-red-800 text-sm px-3 py-2">
 {error.message || 'Could not load messages.'}
 </div>
 )}
 {!isLoading && !error && messages.length === 0 && (
 <p className="text-sm text-gray-500 text-center py-6">No messages yet. Say hello to start the thread.</p>
 )}
 {messages.map((m) => {
 const isMine = m.user_id === user?.id;
 const name = m.user?.name || m.user?.email || 'User';
 return (
 <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
 <div className={`max-w-[85%] px-3 py-2 text-sm ${roleBubbleClass(m.sender_role, isMine)}`}>
 {!isMine && (
 <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80 mb-1">
 {name} · {roleLabel(m.sender_role)}
 </div>
 )}
 <p className="whitespace-pre-wrap break-words">{m.body}</p>
 <p className={`text-[10px] mt-1 ${isMine ? 'text-gray-300' : 'text-gray-500'}`}>
 {m.created_at
 ? new Date(m.created_at).toLocaleString(undefined, {
 month: 'short',
 day: 'numeric',
 hour: '2-digit',
 minute: '2-digit',
 })
 : ''}
 </p>
 </div>
 </div>
 );
 })}
 <div ref={bottomRef} />
 </div>

 {typingText && (
 <p className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-50 bg-surface italic" aria-live="polite">
 {typingText}
 </p>
 )}

 <form onSubmit={handleSend} className="border-t border-gray-100 p-3 bg-surface-muted flex gap-2">
 <textarea
 value={draft}
 onChange={handleDraftChange}
 onKeyDown={handleKeyDown}
 rows={2}
 maxLength={5000}
 placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
 className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-black focus:border-black"
 />
 <button
 type="submit"
 disabled={sendMutation.isPending || !draft.trim()}
 className="self-end inline-flex items-center justify-center px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-40"
 >
 {sendMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
 </button>
 </form>
 {sendMutation.isError && (
 <p className="text-xs text-red-600 px-3 pb-2">{sendMutation.error?.message || 'Send failed.'}</p>
 )}
 </div>
 );
};

export default BookingChatPanel;
